/**
 * Base class for all Web Component controls
 * Provides reactive binding system with {{variable}} syntax
 */

import { composeMixins, ControlMixin, ButtonActionMixin, AttributeHelpersMixin, AccordionAwareMixin } from '../mixins/index.js';

/**
 * Template cache (shared across all components)
 * Maps template ID -> DOM template element
 */
const templateCache = new Map();

/**
 * Template load promises (prevents duplicate fetches)
 * Maps template ID -> Promise
 */
const templateLoadPromises = new Map();

/**
 * ControlElement - Base class for reactive Web Component controls
 *
 * Uses mixins to implement Control interface compatible with ControlManager.
 * Mixins provide:
 * - ControlMixin: getValue/setValue/reset/attachListeners/saveToSettings/restoreFromSettings/triggerChange
 * - ButtonActionMixin: handleButtonAction/registerActionButtons
 * - AttributeHelpersMixin: getNumberAttribute/getBooleanAttribute
 * - AccordionAwareMixin: Automatic accordion resize on content changes
 *
 * ControlElement adds:
 * - Reactive binding system with {{variable}} template syntax
 * - Template processing (inline, external, or direct innerHTML)
 * - Helper methods for element finding and listener attachment
 */
export class ControlElement extends composeMixins(
    HTMLElement,
    ControlMixin,
    ButtonActionMixin,
    AttributeHelpersMixin,
    AccordionAwareMixin
) {
    constructor() {
        super();

        // Transform support for linear scaling (e.g., slider 0-100 → value 0.0-1.0)
        this.transform = 1.0;

        // Binding system
        this._bindings = new Map(); // variable name -> Set of {element, type, attr}
        this._boundProperties = new Set(); // properties exposed via bind-* attributes

        // Internal state
        this._initialized = false;
    }

    /**
     * Called when element is connected to DOM
     */
    connectedCallback() {
        if (this._initialized) return;

        // Set id if not present (HTMLElement.id is automatically synced with attribute)
        if (!this.id) {
            this.id = this.generateId();
        }

        // Initialize control properties from mixins (settingsKey, defaultValue)
        this.initializeControlProperties();

        // Initialize properties from attributes
        this.initializeProperties();

        // Discover which properties are exposed via bind-* attributes
        this.discoverBoundProperties();

        // Initialize template (async wrapper for lazy loading)
        this.initializeTemplate();
    }

    /**
     * Initialize template (async wrapper to support lazy loading)
     */
    async initializeTemplate() {
        // Process template (may be async if loading from file)
        await this.processTemplate();

        // Attach event listeners
        this.attachInternalListeners();

        this._initialized = true;
    }

    /**
     * Generate unique ID
     */
    generateId() {
        return `control-${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Initialize properties from attributes (override in subclasses)
     */
    initializeProperties() {
        // Override in subclasses to read attributes and set properties
    }

    /**
     * Discover which properties are bound via bind-* attributes
     */
    discoverBoundProperties() {
        for (const attr of this.attributes) {
            if (attr.name.startsWith('bind-')) {
                const propName = attr.value; // e.g., bind-label="label" -> "label"
                this._boundProperties.add(propName);
            }
        }
    }

    /**
     * Process template: replace {{placeholders}} and register bind-* elements
     * Supports four modes (in order of priority):
     * 1. External template via template="id" attribute
     * 2. Auto-discovered template by component name (lazy loaded if needed)
     * 3. Inline <template> child element
     * 4. Direct innerHTML
     */
    async processTemplate() {
        let templateContent = null;

        // 1. Check for explicit external template reference
        const templateId = this.getAttribute('template');
        if (templateId) {
            const externalTemplate = document.getElementById(templateId);
            if (externalTemplate && externalTemplate.tagName === 'TEMPLATE') {
                templateContent = externalTemplate.content.cloneNode(true);
            }
        }

        // 2. Auto-discover template by component name (e.g., "linear-slider" -> "tmpl-linear-slider")
        if (!templateContent) {
            const componentName = this.tagName.toLowerCase();
            const autoTemplateId = `tmpl-${componentName}`;

            // Check if template is already in DOM (production mode)
            let autoTemplate = document.getElementById(autoTemplateId);

            // If not in DOM, try to load from file (development mode)
            if (!autoTemplate) {
                autoTemplate = await ControlElement.loadTemplateFromFile(componentName, autoTemplateId);
            }

            if (autoTemplate && autoTemplate.tagName === 'TEMPLATE') {
                // Only use auto-discovered template if element is empty or whitespace-only
                const hasContent = this.innerHTML.trim().length > 0;
                if (!hasContent) {
                    templateContent = autoTemplate.content.cloneNode(true);
                }
            }
        }

        // 3. Check for inline <template> child
        if (!templateContent) {
            const inlineTemplate = this.querySelector(':scope > template');
            if (inlineTemplate) {
                templateContent = inlineTemplate.content.cloneNode(true);
            }
        }

        // 4. If we have template content, use it; otherwise use innerHTML
        if (templateContent) {
            // Clear existing content and use template
            this.innerHTML = '';
            this.appendChild(templateContent);
        }

        // First pass: process attributes with {{variable}} syntax
        const walker = document.createTreeWalker(
            this,
            NodeFilter.SHOW_ELEMENT,
            null,
            false
        );

        const attributeBindings = []; // Store for processing after innerHTML update
        let node;

        while (node = walker.nextNode()) {
            if (node === this) continue;

            for (const attr of Array.from(node.attributes)) {
                const matches = attr.value.match(/\{\{(\w+)\}\}/g);
                if (matches) {
                    // Replace {{variable}} with actual value
                    let newValue = attr.value;
                    matches.forEach(match => {
                        const varName = match.slice(2, -2); // Remove {{ and }}
                        // Check if property exists (works with getters/setters)
                        if (varName in this && this[varName] !== undefined) {
                            const value = this[varName];
                            newValue = newValue.replace(match, value);

                            // Store binding info for later registration
                            attributeBindings.push({
                                element: node,
                                attrName: attr.name,
                                varName: varName
                            });
                        }
                    });
                    attr.value = newValue;
                }
            }
        }

        // Second pass: replace {{variable}} in text content
        let html = this.innerHTML;
        html = html.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
            // Check if property exists (works with getters/setters)
            if (varName in this && this[varName] !== undefined) {
                return this.formatValue(this[varName]);
            }
            return match; // Leave unchanged if property doesn't exist
        });

        // Update innerHTML with replaced placeholders
        this.innerHTML = html;

        // Register attribute bindings from first pass
        // We need to re-query elements since innerHTML was replaced
        for (const binding of attributeBindings) {
            // Find the element again (it was recreated by innerHTML assignment)
            // Use a more robust selector based on tag name and position
            this.registerAttributeBinding(binding.varName, binding.attrName);
        }

        // Find all elements with bind-* attributes and register them
        this.registerBindings(this);
    }

    /**
     * Register attribute bindings for all elements with a specific attribute
     */
    registerAttributeBinding(varName, attrName) {
        const elements = this.querySelectorAll(`[${attrName}]`);
        for (const element of elements) {
            // Check if this attribute's value references our variable
            // (it should, since we found it in the first pass)
            if (!this._bindings.has(varName)) {
                this._bindings.set(varName, new Set());
            }

            const bindings = this._bindings.get(varName);
            bindings.add({ element, type: 'attr', attr: attrName });
        }
    }

    /**
     * Recursively find and register all bind-* attributes in children
     */
    registerBindings(root) {
        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_ELEMENT,
            null,
            false
        );

        let node;
        while (node = walker.nextNode()) {
            // Skip the root element itself
            if (node === this) continue;

            for (const attr of node.attributes) {
                if (attr.name.startsWith('bind-')) {
                    this.registerBinding(node, attr.name, attr.value);
                }
            }
        }
    }

    /**
     * Register a single binding
     * @param {Element} element - The element to bind
     * @param {string} bindAttr - The bind attribute name (e.g., "bind-text", "bind-attr-value")
     * @param {string} varName - The variable name to bind to
     */
    registerBinding(element, bindAttr, varName) {
        if (!this._bindings.has(varName)) {
            this._bindings.set(varName, new Set());
        }

        const bindings = this._bindings.get(varName);

        if (bindAttr === 'bind-text') {
            bindings.add({ element, type: 'text', attr: null });
        } else if (bindAttr.startsWith('bind-attr-')) {
            const attrName = bindAttr.substring('bind-attr-'.length);
            bindings.add({ element, type: 'attr', attr: attrName });
        } else if (bindAttr === 'bind-value') {
            bindings.add({ element, type: 'value', attr: null });
        } else if (bindAttr.startsWith('bind-class-')) {
            const className = bindAttr.substring('bind-class-'.length);
            bindings.add({ element, type: 'class', attr: className });
        } else if (bindAttr === 'bind-show') {
            bindings.add({ element, type: 'show', attr: null });
        }
    }

    /**
     * Update all bindings for a given property
     */
    updateBindings(propName, value) {
        if (!this._bindings.has(propName)) return;

        const bindings = this._bindings.get(propName);
        for (const binding of bindings) {
            switch (binding.type) {
                case 'text':
                    binding.element.textContent = this.formatValue(value);
                    break;
                case 'attr':
                    binding.element.setAttribute(binding.attr, value);
                    break;
                case 'value':
                    binding.element.value = value;
                    break;
                case 'class':
                    binding.element.classList.toggle(binding.attr, !!value);
                    break;
                case 'show':
                    binding.element.style.display = value ? '' : 'none';
                    break;
            }
        }
    }

    /**
     * Format a value for display (can be overridden)
     */
    formatValue(value) {
        if (value == null) return '';
        return String(value);
    }

    /**
     * Attach internal event listeners (override in subclasses)
     */
    attachInternalListeners() {
        // Override in subclasses
    }

    /**
     * Get current value from control (override in subclasses)
     */
    getValue() {
        throw new Error('getValue() must be implemented by subclass');
    }

    /**
     * Set value in control (override in subclasses)
     */
    setValue(value) {
        throw new Error('setValue() must be implemented by subclass');
    }

    /**
     * Note: reset(), handleButtonAction(), attachListeners(), saveToSettings(),
     * restoreFromSettings(), triggerChange(), getNumberAttribute(), and
     * getBooleanAttribute() are provided by mixins (ControlMixin, ButtonActionMixin,
     * AttributeHelpersMixin).
     */

    /**
     * Helper: Find input element
     * Looks for data-role="input" or specific type, or any input
     */
    findInput(type = null, role = 'input') {
        // Try data-role first
        let input = this.querySelector(`[data-role="${role}"]`);
        if (input) return input;

        // Try by type
        if (type) {
            input = this.querySelector(`input[type="${type}"]`);
            if (input) return input;
        }

        // Fallback to any input
        return this.querySelector('input');
    }

    /**
     * Note: registerActionButtons() is provided by ButtonActionMixin
     */

    /**
     * Helper: Create reactive property
     * Creates a getter/setter that automatically calls updateBindings()
     *
     * @param {string} name - Property name
     * @param {*} defaultValue - Initial value
     *
     * Example:
     * this.createReactiveProperty('value', 50);
     * // Creates this._value = 50 and this.value getter/setter
     */
    createReactiveProperty(name, defaultValue) {
        const privateName = `_${name}`;
        this[privateName] = defaultValue;

        Object.defineProperty(this, name, {
            get() {
                return this[privateName];
            },
            set(newValue) {
                this[privateName] = newValue;
                this.updateBindings(name, newValue);
            },
            enumerable: true,
            configurable: true
        });
    }

    /**
     * Helper: Create multiple reactive properties from config
     *
     * @param {Object} config - Object mapping property names to default values
     *
     * Example:
     * this.createReactiveProperties({
     *   label: 'Value',
     *   value: 50,
     *   min: 0,
     *   max: 100
     * });
     */
    createReactiveProperties(config) {
        for (const [name, defaultValue] of Object.entries(config)) {
            this.createReactiveProperty(name, defaultValue);
        }
    }

    /**
     * Helper: Add input listener with debouncing
     *
     * @param {HTMLElement} element - Element to listen to
     * @param {Function} callback - Callback function
     * @param {number} debounce - Debounce time in ms (0 = no debounce)
     */
    addInputListener(element, callback, debounce = 0) {
        if (!element) return;

        if (debounce > 0) {
            let timeout;
            element.addEventListener('input', () => {
                clearTimeout(timeout);
                timeout = setTimeout(callback, debounce);
            });
        } else {
            element.addEventListener('input', callback);
        }
    }

    /**
     * Helper: Find element by role
     * Uses data-role attribute or CSS selector fallback
     *
     * @param {string} role - Role name
     * @param {string} fallback - Fallback CSS selector
     *
     * Example:
     * this.findByRole('slider', 'input[type="range"]');
     */
    findByRole(role, fallback = null) {
        let element = this.querySelector(`[data-role="${role}"]`);
        if (!element && fallback) {
            element = this.querySelector(fallback);
        }
        return element;
    }

    /**
     * Helper: Find all elements by role
     *
     * @param {string} role - Role name
     *
     * Example:
     * this.findAllByRole('option'); // Returns all [data-role="option"]
     */
    findAllByRole(role) {
        return this.querySelectorAll(`[data-role="${role}"]`);
    }

    /**
     * Static method: Load template from file (development mode)
     * In production, templates are pre-injected into DOM by build script
     *
     * @param {string} componentName - Component name (e.g., "linear-slider")
     * @param {string} templateId - Template ID (e.g., "tmpl-linear-slider")
     * @returns {Promise<HTMLTemplateElement|null>} Template element or null
     */
    static async loadTemplateFromFile(componentName, templateId) {
        // Check cache first
        if (templateCache.has(templateId)) {
            return templateCache.get(templateId);
        }

        // Check if template already exists in DOM (production mode with inlined templates)
        const existingTemplate = document.getElementById(templateId);
        if (existingTemplate) {
            templateCache.set(templateId, existingTemplate);
            return existingTemplate;
        }

        // Check if already loading (prevent duplicate fetches)
        if (templateLoadPromises.has(templateId)) {
            await templateLoadPromises.get(templateId);
            return templateCache.get(templateId);
        }

        // Start loading (development mode only)
        const loadPromise = (async () => {
            try {
                const response = await fetch(`src/ui/web-components/templates/${componentName}.html`);
                if (response.ok) {
                    const html = await response.text();
                    const container = document.createElement('div');
                    container.innerHTML = html;
                    const template = container.querySelector('template');

                    if (template) {
                        // Add template to DOM for future use
                        document.body.appendChild(template);
                        templateCache.set(templateId, template);
                        return template;
                    }
                }
            } catch (error) {
                // Silently fail - component will use innerHTML fallback
                console.debug(`Template not found for ${componentName}, using inline HTML`);
            }

            templateCache.set(templateId, null);
            return null;
        })();

        templateLoadPromises.set(templateId, loadPromise);
        const result = await loadPromise;
        templateLoadPromises.delete(templateId);

        return result;
    }
}
